import { useState } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db6f4ci2sgkaik1uneh0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('website_alt');

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请完整填写姓名、邮箱和留言内容。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('请填写有效的邮箱地址。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('留言内容不能超过 500 个字符。');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setStatus('success');
        setFormError('');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg && serverMsg !== '' ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form-6080yy"
      onSubmit={handleSubmit}
      className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            您的姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入姓名"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱地址
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入邮箱"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          maxLength={500}
          required
          placeholder="请输入您的建议或想咨询的内容（最多 500 字）"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-ghi-6080"
      />

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {status === 'submitting' ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-background-50/40 border-t-background-50 animate-spin-slow"></span>
              正在提交…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
              提交留言
            </>
          )}
        </button>
        {status === 'success' && (
          <p className="text-sm text-accent-700 flex items-center gap-1">
            <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center"></i>
            留言已提交，感谢你的反馈！
          </p>
        )}
        {status === 'error' && formError && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}